import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Route, ChevronRight, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { JOURNEY_STAGES, STAGE_ATTRIBUTION, fmtStagePct } from '../../lib/journeyStages';

const fmtPp = (delta) => {
  const pp = Math.round(Math.abs(delta) * 1000) / 10;
  return `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${pp}pp`;
};

function Connector({ delta }) {
  const tone = delta == null
    ? 'text-slate-400 bg-slate-50 border-slate-200'
    : delta < 0
      ? 'text-rose-700 bg-rose-50 border-rose-200'
      : delta > 0
        ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
        : 'text-slate-500 bg-slate-50 border-slate-200';
  return (
    <div className="flex flex-col items-center justify-start px-1 flex-shrink-0 pt-9">
      <ChevronRight className="h-4 w-4 text-slate-300" />
      <span className={cn('mt-1 rounded-full border px-1.5 py-0.5 text-[10px] font-bold tabular-nums', tone)}>
        {delta == null ? '—' : fmtPp(delta)}
      </span>
    </div>
  );
}

function StageNode({ stage, onStageClick }) {
  const attribution = STAGE_ATTRIBUTION[stage.key] || [];
  const isNav = stage.key === 'navigation';
  const clickable = !!(stage.tab && onStageClick);
  const Wrapper = clickable ? 'button' : 'div';
  return (
    <Wrapper
      type={clickable ? 'button' : undefined}
      onClick={clickable ? () => onStageClick(stage.tab) : undefined}
      data-testid={`chain-stage-${stage.key}`}
      title={clickable ? `${stage.blurb} — open the ${stage.label} deep-dive` : stage.blurb}
      className={cn(
        'flex-1 min-w-[140px] rounded-lg border px-3 py-2.5 text-left transition-colors',
        isNav ? 'border-cyan-200 bg-cyan-50/60' : 'border-slate-200 bg-white',
        clickable && 'hover:bg-slate-50 cursor-pointer',
      )}
    >
      <p className="text-2xl font-extrabold tabular-nums leading-tight" style={{ color: stage.color }}>
        {fmtStagePct(stage.value)}
      </p>
      <p className="text-xs font-semibold text-slate-800 mt-0.5">{stage.label}</p>
      <p className="text-[10px] text-slate-400 truncate" title={stage.driver}>via {stage.driver}</p>
      <div className="h-1.5 mt-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.max(0, Math.min(100, (stage.value ?? 0) * 100))}%`,
            backgroundColor: stage.color,
          }}
        />
      </div>
      {/* Journey stage attribution — why this metric matters downstream */}
      <div className="mt-2 pt-1.5 border-t border-slate-100">
        <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Contributes to</p>
        {attribution.map((a) => (
          <div key={a.stage} className="flex items-center justify-between text-[10px] text-slate-500">
            <span className="truncate">→ {a.stage}</span>
            <span className="tabular-nums font-medium">{a.pct}%</span>
          </div>
        ))}
      </div>
    </Wrapper>
  );
}

/**
 * JourneyConversionChain — Sankey-style chain showing where capability
 * leaks before it becomes value: Capability → Visibility → Confidence
 * → Coordination → Navigation, with the conversion gain/loss between
 * each stage. The biggest loss is called out as the Missing Middle.
 */
export function JourneyConversionChain({ current, onStageClick }) {
  if (!current) return null;

  const stages = JOURNEY_STAGES.map((s) => ({ ...s, value: current[s.metric] ?? 0 }));
  stages.forEach((s, i) => {
    s.delta = i === 0 ? null : (s.value ?? 0) - (stages[i - 1].value ?? 0);
  });

  const losses = stages.filter((s) => s.delta != null && s.delta < 0);
  const biggest = losses.length ? losses.reduce((a, b) => (b.delta < a.delta ? b : a)) : null;
  const biggestIdx = biggest ? stages.findIndex((s) => s.key === biggest.key) : -1;

  return (
    <Card data-testid="journey-conversion-chain">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Route className="h-4 w-4 text-cyan-700" />
          Journey Conversion Chain
        </CardTitle>
        <p className="text-xs text-slate-500 italic">What is preventing capability from becoming value?</p>
      </CardHeader>
      <CardContent>
        <div className="flex items-stretch overflow-x-auto pb-1 gap-0.5">
          {stages.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 && <Connector delta={s.delta} />}
              <StageNode stage={s} onStageClick={onStageClick} />
            </React.Fragment>
          ))}
        </div>
        {biggest && biggestIdx > 0 && (
          <div
            className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
            data-testid="largest-conversion-loss"
          >
            <TrendingDown className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800 leading-relaxed">
              <strong>Largest conversion loss:</strong> {stages[biggestIdx - 1].label} → {biggest.label}{' '}
              ({fmtPp(biggest.delta)}). This is where your Missing Middle leaks the most value —{' '}
              {biggest.blurb.toLowerCase()} is the constraint to fix first.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
